import React, { useEffect, useMemo, useState } from "react";
import {
    createEnterprise,
    getEnterpriseByCode,
    getEnterprises,
    getEnterpriseViewDetail,
    getEnterpriseViewList,
    updateEnterprise,
} from "../api/enterpriseApi";
import developerApi from "../api/developerApi";
import "./EnterprisesPage.css";

const { getDataFormats } = developerApi;

const emptyValue = "—";

const HIDDEN_HELP_TEXT_FIELDS = new Set(["data_format", "branch_id", "order_fetcher"]);

const DEFAULT_ENTERPRISE_VALUES = {
    enterprise_code: "",
    enterprise_name: "",
    data_format: "",
    branch_id: "",
    catalog_upload_frequency: "",
    stock_upload_frequency: "",
    catalog_enabled: true,
    stock_enabled: true,
    business_runtime_mode: "baseline",
    business_stock_mode: "baseline_legacy",
    order_fetcher: false,
    tabletki_login: "",
    tabletki_password: "",
    auto_confirm: false,
    discount_rate: "",
    stock_correction: false,
    token: "",
    single_store: false,
    store_serial: "",
    google_drive_folder_id_ref: "",
    google_drive_folder_id_rest: "",
    last_stock_upload: null,
    last_catalog_upload: null,
};

const FALLBACK_FIELD_META = [
    { key: "enterprise_code", label: "Код предприятия", field_type: "text", readonly: false },
    { key: "enterprise_name", label: "Название предприятия", field_type: "text", readonly: false },
    {
        key: "data_format",
        label: "Формат данных",
        field_type: "select",
        readonly: false,
        help_text: null,
    },
    { key: "branch_id", label: "Branch ID", field_type: "text", readonly: false, help_text: null },
    { key: "catalog_upload_frequency", label: "Частота загрузки каталога", field_type: "number", readonly: false },
    { key: "stock_upload_frequency", label: "Частота загрузки остатков", field_type: "number", readonly: false },
    { key: "catalog_stock_enabled", label: "Выгрузка каталога и остатков", field_type: "checkbox", readonly: false, help_text: null },
    {
        key: "order_fetcher",
        label: "Получение заказов",
        field_type: "checkbox",
        readonly: false,
        help_text: null,
    },
    { key: "tabletki_login", label: "Логин Tabletki", field_type: "text", readonly: false },
    { key: "tabletki_password", label: "Пароль Tabletki", field_type: "password", readonly: false },
    { key: "auto_confirm", label: "Автоматическое бронирование", field_type: "checkbox", readonly: false },
    { key: "discount_rate", label: "Размер скидки", field_type: "number", readonly: false },
    { key: "stock_correction", label: "Коррекция остатков", field_type: "checkbox", readonly: false },
    {
        key: "token",
        label: "Токен / URL / ключ подключения",
        field_type: "text",
        readonly: false,
        help_text: "Используется как источник подключения для выбранного формата.",
    },
    {
        key: "single_store",
        label: "Single Store",
        field_type: "checkbox",
        readonly: false,
        help_text: null,
    },
    { key: "store_serial", label: "Store Serial", field_type: "text", readonly: false },
    { key: "google_drive_folder_id_ref", label: "Google Drive Folder ID для каталога", field_type: "text", readonly: false },
    { key: "google_drive_folder_id_rest", label: "Google Drive Folder ID для остатков", field_type: "text", readonly: false },
    { key: "last_stock_upload", label: "Последняя загрузка остатков", field_type: "datetime", readonly: true },
    { key: "last_catalog_upload", label: "Последняя загрузка каталога", field_type: "datetime", readonly: true },
];

const FALLBACK_SECTIONS = [
    { key: "main", title: "Основное", collapsible: false, default_open: true, field_keys: ["enterprise_code", "enterprise_name", "data_format"] },
    { key: "orders_export", title: "Заказы и экспорт", collapsible: false, default_open: true, field_keys: ["tabletki_login", "tabletki_password", "branch_id", "auto_confirm", "discount_rate", "stock_correction", "order_fetcher"] },
    { key: "scheduler", title: "Расписание", collapsible: false, default_open: true, field_keys: ["catalog_upload_frequency", "stock_upload_frequency", "catalog_stock_enabled"] },
    { key: "source", title: "Источник и формат", collapsible: false, default_open: true, field_keys: ["token"] },
    {
        key: "format_fields",
        title: "Доп. поля формата",
        description: null,
        collapsible: true,
        default_open: false,
        field_keys: ["single_store", "store_serial", "google_drive_folder_id_ref", "google_drive_folder_id_rest"],
    },
    {
        key: "runtime",
        title: "Служебное",
        description: null,
        collapsible: true,
        default_open: false,
        field_keys: ["last_stock_upload", "last_catalog_upload"],
    },
];

const formatDateTime = (value) => {
    if (!value) {
        return emptyValue;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("uk-UA");
};

const isFileRelatedFormat = (dataFormat) => {
    const normalized = String(dataFormat || "").trim();
    return [
        "GoogleDrive",
        "JetVet",
        "TorgsoftGoogle",
        "TorgsoftGoogleMulti",
        "Ftp",
        "FtpMulti",
        "FtpZoomagazin",
        "FtpTabletki",
    ].includes(normalized);
};

const hasFormatSpecificFields = (values = {}) => (
    Boolean(values.single_store)
    || Boolean(String(values.store_serial || "").trim())
    || Boolean(String(values.google_drive_folder_id_ref || "").trim())
    || Boolean(String(values.google_drive_folder_id_rest || "").trim())
);

const buildFallbackListFromRawEnterprises = (items = []) => items.map((enterprise) => ({
    enterprise_code: enterprise.enterprise_code,
    enterprise_name: enterprise.enterprise_name,
    data_format: enterprise.data_format || null,
    branch_id: enterprise.branch_id || null,
    catalog_upload_frequency: enterprise.catalog_upload_frequency ?? null,
    stock_upload_frequency: enterprise.stock_upload_frequency ?? null,
    catalog_enabled: enterprise.catalog_enabled !== false,
    stock_enabled: enterprise.stock_enabled !== false,
    order_fetcher: Boolean(enterprise.order_fetcher),
    last_stock_upload: enterprise.last_stock_upload || null,
    last_catalog_upload: enterprise.last_catalog_upload || null,
    is_blank_format: String(enterprise.data_format || "").trim() === "Blank",
    has_format_specific_fields: (
        isFileRelatedFormat(enterprise.data_format)
        || hasFormatSpecificFields(enterprise)
    ),
}));

const buildFallbackDetailFromRawEnterprise = (enterprise) => ({
    enterprise_code: enterprise.enterprise_code,
    enterprise_name: enterprise.enterprise_name,
    data_format: enterprise.data_format || null,
    catalog_enabled: enterprise.catalog_enabled !== false,
    stock_enabled: enterprise.stock_enabled !== false,
    values: {
        ...DEFAULT_ENTERPRISE_VALUES,
        ...enterprise,
        catalog_enabled: enterprise.catalog_enabled !== false,
        stock_enabled: enterprise.stock_enabled !== false,
    },
    field_meta: FALLBACK_FIELD_META,
    sections: FALLBACK_SECTIONS,
    show_format_fields_block: (
        isFileRelatedFormat(enterprise.data_format)
        || hasFormatSpecificFields(enterprise)
    ),
    show_runtime_block: true,
});

const normalizeDraftValues = (values = {}) => ({
    ...DEFAULT_ENTERPRISE_VALUES,
    ...values,
    catalog_enabled: values.catalog_enabled !== false,
    stock_enabled: values.stock_enabled !== false,
    order_fetcher: Boolean(values.order_fetcher),
    auto_confirm: Boolean(values.auto_confirm),
    stock_correction: Boolean(values.stock_correction),
    single_store: Boolean(values.single_store),
});

const EntTile = ({ label, value }) => (
    <div className="ent-tile">
        <span className="l">{label}</span>
        <span className="v">{value || emptyValue}</span>
    </div>
);

const EntCheck = ({ label, checked, onChange, disabled = false }) => (
    <button
        type="button"
        className={checked ? "ent-check ck" : "ent-check"}
        disabled={disabled}
        onClick={() => !disabled && onChange && onChange(!checked)}
    >
        <span className="box">{checked ? "✓" : ""}</span>
        <span>{label}</span>
    </button>
);

const EnterprisePanel = ({ embedded = false }) => {
    const [enterpriseList, setEnterpriseList] = useState([]);
    const [dataFormats, setDataFormats] = useState([]);
    const [selectedEnterpriseCode, setSelectedEnterpriseCode] = useState("");
    const [detailView, setDetailView] = useState(null);
    const [draftValues, setDraftValues] = useState(DEFAULT_ENTERPRISE_VALUES);
    const [isCreating, setIsCreating] = useState(false);
    const [listLoading, setListLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [listError, setListError] = useState("");
    const [detailError, setDetailError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [saveSuccess, setSaveSuccess] = useState("");
    const [showOnlyActive, setShowOnlyActive] = useState(false);
    const [activeSectionKey, setActiveSectionKey] = useState("main");

    const loadEnterpriseList = async () => {
        setListLoading(true);
        setListError("");
        try {
            let data;
            try {
                data = await getEnterpriseViewList();
            } catch (viewError) {
                console.error("Error loading enterprise view list, falling back to raw enterprise list:", viewError);
                const rawEnterprises = await getEnterprises();
                data = buildFallbackListFromRawEnterprises(rawEnterprises);
            }
            setEnterpriseList(data);
            if (!selectedEnterpriseCode && data.length > 0 && !isCreating) {
                setSelectedEnterpriseCode(data[0].enterprise_code);
            }
        } catch (error) {
            console.error("Error loading enterprise view list:", error);
            setListError("Не удалось загрузить список предприятий.");
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const formats = await getDataFormats();
                setDataFormats(formats);
            } catch (error) {
                console.error("Error loading data formats:", error);
            }
            await loadEnterpriseList();
        };

        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedEnterpriseCode || isCreating) {
            return;
        }

        const loadDetail = async () => {
            setDetailLoading(true);
            setDetailError("");
            try {
                let data;
                try {
                    data = await getEnterpriseViewDetail(selectedEnterpriseCode);
                } catch (viewError) {
                    console.error("Error loading enterprise view detail, falling back to raw enterprise detail:", viewError);
                    const rawEnterprise = await getEnterpriseByCode(selectedEnterpriseCode);
                    data = buildFallbackDetailFromRawEnterprise(rawEnterprise);
                }
                setDetailView(data);
                setDraftValues(normalizeDraftValues(data.values));
            } catch (error) {
                console.error("Error loading enterprise detail:", error);
                setDetailError("Не удалось загрузить детальную информацию по предприятию.");
                setDetailView(null);
            } finally {
                setDetailLoading(false);
            }
        };

        loadDetail();
    }, [selectedEnterpriseCode, isCreating]);

    const fieldMetaMap = useMemo(() => {
        const backendMeta = detailView?.field_meta || [];
        const meta = [...backendMeta];
        const existingKeys = new Set(backendMeta.map((field) => field.key));

        FALLBACK_FIELD_META.forEach((field) => {
            if (!existingKeys.has(field.key)) {
                meta.push(field);
            }
        });

        return meta.reduce((acc, field) => {
            acc[field.key] = field;
            return acc;
        }, {});
    }, [detailView]);

    const sectionList = useMemo(() => {
        const sourceSections = detailView?.sections || FALLBACK_SECTIONS;
        const normalizedSections = sourceSections.map((section) => {
            if (section.key === "main") {
                return {
                    ...section,
                    title: section.title || "Основное",
                    field_keys: ["enterprise_code", "enterprise_name", "data_format"],
                };
            }

            if (section.key === "orders_export") {
                return {
                    ...section,
                    title: section.title || "Заказы и экспорт",
                    field_keys: ["tabletki_login", "tabletki_password", "branch_id", "auto_confirm", "discount_rate", "stock_correction", "order_fetcher"],
                };
            }

            if (section.key === "scheduler") {
                return {
                    ...section,
                    title: section.title || "Расписание",
                    field_keys: ["catalog_upload_frequency", "stock_upload_frequency", "catalog_stock_enabled"],
                };
            }

            if (section.key === "source") {
                return { ...section, title: section.title || "Источник и формат", description: null };
            }

            if (section.key === "format_fields") {
                return { ...section, title: section.title || "Доп. поля формата", description: null };
            }

            if (section.key === "runtime") {
                return { ...section, title: section.title || "Служебное", description: null };
            }

            return section;
        });

        const sectionOrder = ["main", "scheduler", "orders_export", "source", "format_fields", "runtime"];
        return normalizedSections.sort((a, b) => sectionOrder.indexOf(a.key) - sectionOrder.indexOf(b.key));
    }, [detailView]);

    useEffect(() => {
        if (sectionList.length === 0) {
            return;
        }
        if (!sectionList.some((section) => section.key === activeSectionKey)) {
            setActiveSectionKey(sectionList[0].key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionList]);

    const filteredEnterpriseList = useMemo(() => (
        showOnlyActive
            ? enterpriseList.filter((enterprise) => enterprise.catalog_enabled && enterprise.stock_enabled)
            : enterpriseList
    ), [enterpriseList, showOnlyActive]);

    const resetToCreate = () => {
        setIsCreating(true);
        setSelectedEnterpriseCode("");
        setDetailView(null);
        setDetailError("");
        setSaveError("");
        setSaveSuccess("");
        setDraftValues(DEFAULT_ENTERPRISE_VALUES);
        setActiveSectionKey("main");
    };

    const selectEnterprise = (enterpriseCode) => {
        setIsCreating(false);
        setSaveError("");
        setSaveSuccess("");
        setSelectedEnterpriseCode(enterpriseCode);
    };

    const handleFieldChange = (key, value) => {
        if (key === "catalog_stock_enabled") {
            setDraftValues((prev) => ({
                ...prev,
                catalog_enabled: Boolean(value),
                stock_enabled: Boolean(value),
            }));
            return;
        }

        setDraftValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const buildSavePayload = () => ({
        enterprise_code: String(draftValues.enterprise_code || "").trim(),
        enterprise_name: String(draftValues.enterprise_name || "").trim(),
        data_format: String(draftValues.data_format || "").trim() || null,
        branch_id: String(draftValues.branch_id || "").trim(),
        catalog_upload_frequency: draftValues.catalog_upload_frequency === "" ? null : Number(draftValues.catalog_upload_frequency),
        stock_upload_frequency: draftValues.stock_upload_frequency === "" ? null : Number(draftValues.stock_upload_frequency),
        catalog_enabled: Boolean(draftValues.catalog_enabled),
        stock_enabled: Boolean(draftValues.stock_enabled),
        business_runtime_mode: String(draftValues.business_runtime_mode || "baseline"),
        business_stock_mode: String(draftValues.business_stock_mode || "baseline_legacy"),
        order_fetcher: Boolean(draftValues.order_fetcher),
        tabletki_login: String(draftValues.tabletki_login || "").trim() || null,
        tabletki_password: String(draftValues.tabletki_password || "").trim() || null,
        auto_confirm: Boolean(draftValues.auto_confirm),
        discount_rate: draftValues.discount_rate === "" ? null : Number(draftValues.discount_rate),
        stock_correction: Boolean(draftValues.stock_correction),
        token: String(draftValues.token || "").trim() || null,
        single_store: Boolean(draftValues.single_store),
        store_serial: String(draftValues.store_serial || "").trim() || null,
        google_drive_folder_id_ref: String(draftValues.google_drive_folder_id_ref || "").trim() || null,
        google_drive_folder_id_rest: String(draftValues.google_drive_folder_id_rest || "").trim() || null,
        last_stock_upload: draftValues.last_stock_upload || null,
        last_catalog_upload: draftValues.last_catalog_upload || null,
    });

    const handleSave = async () => {
        setSaveError("");
        setSaveSuccess("");

        const payload = buildSavePayload();
        if (!payload.enterprise_code || !payload.enterprise_name || !payload.branch_id) {
            setSaveError("Поля код предприятия, название и Branch ID должны быть заполнены.");
            return;
        }

        try {
            if (isCreating) {
                await createEnterprise(payload);
                setSaveSuccess("Предприятие успешно создано.");
                setIsCreating(false);
                setSelectedEnterpriseCode(payload.enterprise_code);
            } else {
                await updateEnterprise(selectedEnterpriseCode, payload);
                setSaveSuccess("Изменения сохранены.");
            }

            await loadEnterpriseList();
            if (payload.enterprise_code) {
                let viewDetail;
                try {
                    viewDetail = await getEnterpriseViewDetail(payload.enterprise_code);
                } catch (viewError) {
                    console.error("Error reloading enterprise view detail after save, falling back to raw enterprise detail:", viewError);
                    const rawEnterprise = await getEnterpriseByCode(payload.enterprise_code);
                    viewDetail = buildFallbackDetailFromRawEnterprise(rawEnterprise);
                }
                setDetailView(viewDetail);
                setDraftValues(normalizeDraftValues(viewDetail.values));
                setSelectedEnterpriseCode(payload.enterprise_code);
            }
        } catch (error) {
            console.error("Error saving enterprise:", error);
            setSaveError("Не удалось сохранить настройки предприятия.");
        }
    };

    const handleReset = async () => {
        setSaveError("");
        setSaveSuccess("");

        if (isCreating) {
            setDraftValues(DEFAULT_ENTERPRISE_VALUES);
            return;
        }

        if (!selectedEnterpriseCode) {
            return;
        }

        try {
            let data;
            try {
                data = await getEnterpriseViewDetail(selectedEnterpriseCode);
            } catch (viewError) {
                console.error("Error resetting enterprise draft from view detail, falling back to raw enterprise detail:", viewError);
                const rawEnterprise = await getEnterpriseByCode(selectedEnterpriseCode);
                data = buildFallbackDetailFromRawEnterprise(rawEnterprise);
            }
            setDetailView(data);
            setDraftValues(normalizeDraftValues(data.values));
        } catch (error) {
            console.error("Error resetting enterprise draft:", error);
        }
    };

    const renderField = (fieldKey) => {
        const meta = fieldMetaMap[fieldKey];
        if (!meta) {
            return null;
        }

        const value = fieldKey === "catalog_stock_enabled"
            ? Boolean(draftValues.catalog_enabled) && Boolean(draftValues.stock_enabled)
            : draftValues[fieldKey];
        const readonly = Boolean(meta.readonly);
        const isCheckbox = meta.field_type === "checkbox";
        const isReadonlyDisplay = readonly || meta.field_type === "datetime";
        const spanField = fieldKey === "order_fetcher" || fieldKey === "token";

        if (isReadonlyDisplay) {
            return (
                <EntTile
                    key={fieldKey}
                    label={meta.label}
                    value={meta.field_type === "datetime" ? formatDateTime(value) : value}
                />
            );
        }

        if (isCheckbox) {
            return (
                <EntCheck
                    key={fieldKey}
                    label={meta.label}
                    checked={Boolean(value)}
                    onChange={(next) => handleFieldChange(fieldKey, next)}
                />
            );
        }

        return (
            <div key={fieldKey} className={spanField ? "ent-fld ent-col-span" : "ent-fld"}>
                <label>{meta.label}</label>
                {meta.field_type === "select" ? (
                    <select
                        className="ent-inp"
                        value={value || ""}
                        onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                    >
                        <option value="">-- Выберите формат --</option>
                        {dataFormats.map((format) => (
                            <option key={format.id || format.format_name} value={format.format_name}>
                                {format.format_name}
                            </option>
                        ))}
                    </select>
                ) : (
                    <input
                        className="ent-inp"
                        type={meta.field_type === "number" ? "number" : meta.field_type === "password" ? "password" : "text"}
                        value={value ?? ""}
                        onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                    />
                )}
                {meta.help_text && !HIDDEN_HELP_TEXT_FIELDS.has(fieldKey) ? (
                    <div className="help">{meta.help_text}</div>
                ) : null}
            </div>
        );
    };

    const activeSection = sectionList.find((section) => section.key === activeSectionKey) || sectionList[0];
    const activeSectionGridClass = activeSection && (activeSection.key === "main" || activeSection.key === "orders_export")
        ? "ent-grid3"
        : "ent-grid-auto";

    const initial = (draftValues.enterprise_name || "?").trim().charAt(0).toUpperCase() || "?";

    return (
        <div className="ent-page">
            {!embedded && (
                <div className="ent-card" style={{ padding: "20px 24px" }}>
                    <h1 style={{ margin: 0, fontSize: "26px" }}>Настройки предприятий</h1>
                </div>
            )}

            <div className="ent-split">
                <aside className="ent-card ent-picker">
                    <h2>Выбор предприятия</h2>
                    <div className="ent-picker-actions">
                        <button type="button" className="ent-btn ent-btn-primary ent-btn-sm" onClick={resetToCreate}>
                            + Новое предприятие
                        </button>
                        <button type="button" className="ent-btn ent-btn-ghost ent-btn-sm" onClick={loadEnterpriseList}>
                            Обновить список
                        </button>
                    </div>

                    <EntCheck
                        label="Показывать только активные"
                        checked={showOnlyActive}
                        onChange={setShowOnlyActive}
                    />

                    {listLoading ? <div className="ent-mini">Загрузка предприятий…</div> : null}
                    {listError ? <div className="ent-msg err">{listError}</div> : null}
                    {!listLoading && !listError && showOnlyActive && filteredEnterpriseList.length === 0 ? (
                        <div className="ent-mini">Нет активных предприятий</div>
                    ) : null}

                    <div className="ent-list">
                        {filteredEnterpriseList.map((enterprise) => (
                            <button
                                key={enterprise.enterprise_code}
                                type="button"
                                className={
                                    enterprise.enterprise_code === selectedEnterpriseCode && !isCreating
                                        ? "ent-list-item sel"
                                        : "ent-list-item"
                                }
                                onClick={() => selectEnterprise(enterprise.enterprise_code)}
                            >
                                <span className="nm">{enterprise.enterprise_name}</span>
                                <span className="meta">Код: {enterprise.enterprise_code}</span>
                                <span className="badges">
                                    <span className={enterprise.is_blank_format ? "ent-pill warn" : "ent-pill"}>
                                        {enterprise.data_format || "—"}
                                    </span>
                                    {enterprise.order_fetcher ? <span className="ent-pill">orders</span> : null}
                                    {enterprise.has_format_specific_fields ? <span className="ent-pill">file/google</span> : null}
                                </span>
                            </button>
                        ))}
                    </div>
                </aside>

                <section className="ent-detail">
                    <div className="ent-card ent-detail-head">
                        <div className="ent-avatar">{isCreating ? "+" : initial}</div>
                        <div className="ent-who">
                            <span className="t">
                                {isCreating ? "Новое предприятие" : (draftValues.enterprise_name || emptyValue)}
                            </span>
                            <span className="s">
                                {!isCreating ? (
                                    <span className={draftValues.catalog_enabled && draftValues.stock_enabled ? "ent-badge on" : "ent-badge off"}>
                                        <span className="dot" />
                                        {draftValues.catalog_enabled && draftValues.stock_enabled ? "Каталог и остатки включены" : "Частично выключено"}
                                    </span>
                                ) : null}
                                <span>Код {draftValues.enterprise_code || emptyValue}</span>
                                <span>·</span>
                                <span>Формат {draftValues.data_format || emptyValue}</span>
                                <span>·</span>
                                <span>Branch ID {draftValues.branch_id || emptyValue}</span>
                            </span>
                        </div>
                        <div className="ent-acts">
                            <button type="button" className="ent-btn ent-btn-ghost" onClick={handleReset}>Сбросить</button>
                            <button type="button" className="ent-btn ent-btn-primary" onClick={handleSave}>Сохранить</button>
                        </div>
                    </div>

                    {saveError ? <div className="ent-msg err">{saveError}</div> : null}
                    {saveSuccess ? <div className="ent-msg ok">{saveSuccess}</div> : null}
                    {detailError ? <div className="ent-msg err">{detailError}</div> : null}
                    {detailLoading ? <div className="ent-mini">Загрузка детальной информации…</div> : null}

                    <div className="ent-card" style={{ padding: "14px 14px 0" }}>
                        <div className="ent-tabbar" role="tablist">
                            {sectionList.map((section) => (
                                <button
                                    key={section.key}
                                    type="button"
                                    className={activeSectionKey === section.key ? "ent-tab active" : "ent-tab"}
                                    onClick={() => setActiveSectionKey(section.key)}
                                >
                                    {section.title}
                                </button>
                            ))}
                        </div>

                        {activeSection ? (
                            <div className="ent-panel-body">
                                {activeSection.description ? (
                                    <div className="ent-callout"><span className="ic">ⓘ</span><div>{activeSection.description}</div></div>
                                ) : null}
                                <div className="ent-block">
                                    <div className={activeSectionGridClass}>
                                        {activeSection.field_keys.map(renderField)}
                                    </div>
                                </div>
                                <div className="ent-save-bar">
                                    <span className="note">
                                        {isCreating ? "Новое предприятие" : `Код: ${draftValues.enterprise_code || emptyValue}`}
                                    </span>
                                    <button type="button" className="ent-btn ent-btn-ghost" onClick={handleReset}>Отмена</button>
                                    <button type="button" className="ent-btn ent-btn-primary" onClick={handleSave}>Сохранить</button>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default EnterprisePanel;
